import { motion } from "framer-motion";
import { ArrowRight, Star, ShoppingBag, Globe, Trophy, CheckCircle2, Layout, RefreshCw, ShoppingCart, Palette, ExternalLink, Zap, Code2, Rocket, MessageSquare, Send, Mail, ChevronDown, Quote, TrendingUp, Target, AlertCircle, BarChart3, Search, Lightbulb, Info, AlertTriangle, X, Clock, DollarSign, ListChecks, ChevronLeft, ChevronRight, ShieldCheck, Lock } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import PageWrapper from "../components/PageWrapper";
import { SEO } from "../components/SEO";
import { useState, FormEvent, useEffect, lazy, Suspense } from "react";
import { AnimatePresence } from "framer-motion";
import { cn, openCalendlyPopup } from "../lib/utils";
import ScrollReveal from "../components/ScrollReveal";
import { TiltCard } from "../components/TiltCard";

import { Marquee } from "../components/Marquee";
import { WordRotator } from "../components/WordRotator";
import { IntersectionWrapper } from "../components/IntersectionBoundary";
import { GmailIcon, LinkedInIcon, UpworkIcon, WhatsAppIcon } from "../components/icons/SocialIcons";

const Shopify3DEngine = lazy(() => import("../components/Shopify3DEngine"));
const ImpactMetrics = lazy(() => import("../components/ImpactMetrics").then(m => ({ default: m.ImpactMetrics })));

const stats = [
  { label: "Stores Built", value: "20+" },
  { label: "Star Rating", value: "5.0" },
  { label: "Years Experience", value: "3+" },
  { label: "Client Satisfaction", value: "100%" },
];

const services = [
  {
    title: "Store Setup & Build",
    icon: Layout,
    desc: "Complete Shopify store setup from scratch: theme, products, and configuration.",
  },
  {
    title: "Shopify Migration Service",
    icon: RefreshCw,
    desc: "Seamlessly migrate your store from any platform to Shopify without losing data.",
  },
  {
    title: "Dropshipping Stores",
    icon: ShoppingCart,
    desc: "Niche research, supplier setup, and conversion-optimized design for dropshipping.",
  },
  {
    title: "Theme Customization",
    icon: Palette,
    desc: "Custom-designed Shopify store using premium themes tailored to your brand.",
  },
];



const testimonials = [
  {
    name: "Sarah Jenkins",
    rating: 5,
    content: "Sheun Hub completely transformed our online presence. Our conversion rate doubled within the first month after the redesign.",
  },
  {
    name: "Michael Chen",
    rating: 5,
    content: "The level of expertise in Shopify development is unmatched. They handled our complex migration flawlessly without any downtime.",
  },
  {
    name: "Emma Watson",
    rating: 5,
    content: "Incredible attention to detail and UX. Our new custom theme perfectly captures our brand identity while being lightning fast.",
  },
  {
    name: "David Rodriguez",
    rating: 5,
    content: "The best investment we've made for our e-commerce business. The technical SEO improvements alone brought in massive organic traffic.",
  },
];

const SEOReport = ({ data }: { data: any }) => {
  if (!data) return null;
  
  return (
    <div className="space-y-12 animate-in fade-in slide-in-from-bottom-8 duration-700">
      {/* Summary Header */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 bg-navy p-8 rounded-2xl text-white space-y-4 relative overflow-hidden">
          <div className="absolute top-0 right-0 p-8 opacity-5">
            <Globe size={120} />
          </div>
          <div className="flex items-center gap-3 text-[#8B5CF6] relative z-10">
            <Globe size={20} />
            <span className="text-[10px] font-bold uppercase tracking-widest">Store Analysis</span>
          </div>
          <h3 className="text-3xl font-bold relative z-10 break-all">{data.store_summary.store_url}</h3>
          <p className="text-white/60 leading-relaxed relative z-10">{data.store_summary.seo_score_reason}</p>
          <div className="pt-4 flex flex-wrap gap-4 relative z-10">
             <div className="bg-white/10 px-4 py-2 rounded-2xl">
               <p className="text-[8px] text-white/40 uppercase font-bold">Niche</p>
               <p className="font-bold text-[#8B5CF6]">{data.store_summary.detected_niche}</p>
             </div>
             <div className="bg-white/10 px-4 py-2 rounded-2xl">
               <p className="text-[8px] text-white/40 uppercase font-bold">Target Audience</p>
               <p className="font-bold">{data.store_summary.target_audience}</p>
             </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-[#6D28D9] to-[#8B5CF6] p-8 rounded-2xl flex flex-col items-center justify-center text-center space-y-2 shadow-xl shadow-[#6D28D9]/20 text-white">
           <p className="text-white/70 text-[10px] font-bold uppercase tracking-widest">SEO Score</p>
           <p className="text-7xl font-black text-white tracking-tighter">{data.store_summary.current_seo_score}</p>
           <Trophy className="text-white/30" size={40} />
        </div>
      </div>

      {/* Biggest Opportunity */}
      <div className="bg-navy/5 border-2 border-[#6D28D9]/20 p-8 rounded-2xl flex items-start gap-6 group hover:bg-[#6D28D9]/5 transition-colors">
        <div className="w-12 h-12 bg-[#6D28D9] rounded-2xl flex items-center justify-center text-white shrink-0 shadow-lg shadow-[#6D28D9]/20">
          <Zap size={24} />
        </div>
        <div className="space-y-1">
          <p className="text-[10px] font-bold text-navy dark:text-white/40 uppercase tracking-widest">Biggest Opportunity</p>
          <p className="text-xl font-bold text-navy dark:text-white">{data.store_summary.biggest_opportunity}</p>
        </div>
      </div>

      {/* Keywords Grid */}
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Search className="text-[#8B5CF6]" size={24} />
          <h4 className="text-2xl font-bold text-navy dark:text-white">Primary Keywords</h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.keywords.map((kw: any, i: number) => (
            <div key={i} className="bg-white dark:bg-white/5 border border-navy/5 dark:border-white/10 p-6 rounded-3xl hover:shadow-lg transition-shadow group">
              <div className="flex justify-between items-start mb-4">
                <p className="text-lg font-bold text-navy dark:text-white group-hover:text-[#8B5CF6] transition-colors">{kw.keyword}</p>
                <span className={`text-[8px] font-bold uppercase px-2 py-1 rounded-full ${
                  kw.difficulty === 'Easy' ? 'bg-[#6D28D9]/10 text-[#8B5CF6]' : 
                  kw.difficulty === 'Medium' ? 'bg-yellow-500/10 text-yellow-600' : 'bg-red-500/10 text-red-600'
                }`}>
                  {kw.difficulty}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-[10px]">
                <div>
                  <p className="text-navy dark:text-white/40 uppercase font-bold">Volume</p>
                  <p className="font-bold text-navy dark:text-white">{kw.volume}</p>
                </div>
                <div>
                  <p className="text-navy dark:text-white/40 uppercase font-bold">Intent</p>
                  <p className="font-bold text-navy dark:text-white">{kw.intent}</p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-navy/5 dark:border-white/10">
                <p className="text-[10px] text-navy dark:text-white/40 uppercase font-bold mb-1">Where to use</p>
                <p className="text-xs text-navy dark:text-white/70 leading-relaxed">{kw.where_to_use}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Longtail Keywords */}
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <TrendingUp className="text-[#8B5CF6]" size={24} />
          <h4 className="text-2xl font-bold text-navy dark:text-white">Long-tail Opportunities</h4>
        </div>
        <div className="bg-navy p-8 rounded-2xl space-y-6 shadow-2xl">
          {data.longtail.map((lt: any, i: number) => (
            <div key={i} className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-white/10 last:border-0 last:pb-0">
              <div className="space-y-1">
                <p className="text-lg font-bold text-white">{lt.keyword}</p>
                <p className="text-white/40 text-xs">{lt.why_it_converts}</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-[8px] text-white/40 uppercase font-bold">Potential</p>
                  <p className="text-[#8B5CF6] font-bold">{lt.conversion_potential}</p>
                </div>
                <div className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center text-[#8B5CF6]">
                  <CheckCircle2 size={20} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Gaps & Quick Wins */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <AlertCircle className="text-red-500" size={24} />
            <h4 className="text-2xl font-bold text-navy dark:text-white">Critical Gaps</h4>
          </div>
          <div className="space-y-4">
            {data.seo_gaps.map((gap: any, i: number) => (
              <div key={i} className="bg-red-50 dark:bg-red-950/20 border border-red-100 dark:border-red-900/30 p-6 rounded-3xl space-y-3">
                <p className="font-bold text-red-900 dark:text-red-300">{gap.problem}</p>
                <p className="text-xs text-red-700/80 dark:text-red-400 italic">Location: {gap.location}</p>
                <div className="bg-white dark:bg-white/5 p-4 rounded-2xl text-xs text-navy dark:text-white/80 border border-red-200 dark:border-red-900/40">
                  <p className="font-bold text-navy dark:text-white mb-1">Fix:</p>
                  {gap.fix}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <Rocket className="text-[#8B5CF6]" size={24} />
            <h4 className="text-2xl font-bold text-navy dark:text-white">Quick Wins</h4>
          </div>
          <div className="space-y-4">
            {data.quick_wins.map((win: any, i: number) => (
              <div key={i} className="bg-[#6D28D9]/5 dark:bg-white/5 border border-[#6D28D9]/15 dark:border-white/10 p-6 rounded-3xl space-y-3">
                <p className="font-bold text-navy dark:text-white">{win.action}</p>
                <p className="text-xs text-navy dark:text-white/60 italic">Where: {win.where}</p>
                <div className="flex items-center gap-2 text-[#8B5CF6] font-bold text-[10px] uppercase tracking-widest bg-[#6D28D9]/10 w-fit px-3 py-1 rounded-full">
                  <Zap size={14} />
                  Impact: {win.impact}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Competitor Analysis - New Section */}
      {data.competitor_analysis && (
        <div className="space-y-6 pt-12 border-t border-navy/5">
          <div className="flex items-center gap-3">
            <Target className="text-navy dark:text-white" size={24} />
            <h4 className="text-2xl font-bold text-navy dark:text-white">Competitor Gap Analysis</h4>
          </div>
          <div className="bg-navy p-8 md:p-12 rounded-2xl text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 p-12 opacity-5">
              <BarChart3 size={160} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 relative z-10">
              <div className="space-y-6">
                <p className="text-[#8B5CF6] text-[10px] font-bold uppercase tracking-widest">What they have that you don't</p>
                <div className="space-y-4">
                  {data.competitor_analysis.gaps.map((gap: any, i: number) => (
                    <div key={i} className="flex gap-4">
                      <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center text-red-400 shrink-0">
                        <AlertTriangle size={14} />
                      </div>
                      <div className="space-y-1">
                        <p className="font-bold">{gap.title}</p>
                        <p className="text-white/40 text-xs leading-relaxed">{gap.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-6">
                <p className="text-[#8B5CF6] text-[10px] font-bold uppercase tracking-widest">How to outrank them</p>
                <div className="space-y-4">
                  {data.competitor_analysis.outrank_strategy.map((strategy: any, i: number) => (
                    <div key={i} className="flex gap-4">
                      <div className="w-6 h-6 rounded-full bg-[#6D28D9]/20 flex items-center justify-center text-[#8B5CF6] shrink-0">
                        <CheckCircle2 size={14} />
                      </div>
                      <div className="space-y-1">
                        <p className="font-bold">{strategy.title}</p>
                        <p className="text-white/40 text-xs leading-relaxed">{strategy.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ServiceModal = ({ service, onClose }: { service: any; onClose: () => void }) => {
  if (!service) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-navy/90 backdrop-blur-xl"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        className="relative w-full max-w-4xl bg-white dark:bg-navy rounded-2xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto border border-navy/10 dark:border-white/10"
      >
        <button
          onClick={onClose}
          className="absolute top-6 right-6 w-12 h-12 bg-light dark:bg-white/10 rounded-full flex items-center justify-center text-navy dark:text-white hover:bg-[#6D28D9] hover:text-white transition-all z-10"
        >
          <X size={24} />
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-5 bg-navy dark:bg-navy-light p-8 md:p-12 text-white space-y-8">
            <div className="w-16 h-16 bg-gradient-to-br from-[#6D28D9] to-[#8B5CF6] rounded-2xl flex items-center justify-center text-white">
              <service.icon size={32} />
            </div>
            <div className="space-y-4">
              <h3 className="text-4xl font-bold leading-tight">{service.title}</h3>
              <p className="text-white/60 leading-relaxed">{service.fullDesc}</p>
            </div>
            <div className="space-y-4 pt-8 border-t border-white/10">
              <div className="flex items-center gap-4">
                <Clock className="text-[#8B5CF6]" size={20} />
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/40">Timeline</p>
                  <p className="font-bold">{service.timeline}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <DollarSign className="text-[#8B5CF6]" size={20} />
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/40">Starting From</p>
                  <p className="font-bold">{service.price}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 p-8 md:p-12 space-y-10">
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <ListChecks className="text-[#8B5CF6]" size={24} />
                <h4 className="text-2xl font-bold text-navy dark:text-white">Service Roadmap</h4>
              </div>
              <div className="space-y-6 relative before:absolute before:left-4 before:top-2 before:bottom-2 before:w-px before:bg-navy/5 dark:before:bg-white/10">
                {service.roadmap.map((step: any, i: number) => (
                  <div key={i} className="relative pl-12 group">
                    <div className="absolute left-0 top-1 w-8 h-8 bg-light dark:bg-white/10 rounded-full border-2 border-navy/5 dark:border-white/10 flex items-center justify-center text-xs font-bold text-navy dark:text-white group-hover:bg-[#6D28D9] group-hover:border-[#6D28D9] group-hover:text-white transition-all">
                      {i + 1}
                    </div>
                    <div className="space-y-1">
                      <p className="font-bold text-navy dark:text-white">{step.title}</p>
                      <p className="text-sm text-navy/70 dark:text-white/60 leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-8 border-t border-navy/5 dark:border-white/10">
              <a
                href="#contact"
                onClick={(e) => {
                  onClose();
                  // Smooth scroll to contact
                  const contact = document.getElementById('contact');
                  if (contact) {
                    e.preventDefault();
                    contact.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="w-full bg-[#6D28D9] text-white py-5 rounded-full font-bold text-lg hover:bg-[#8B5CF6] transition-all flex items-center justify-center gap-3 shadow-xl shadow-[#6D28D9]/20"
              >
                Get Started with {service.title} <ArrowRight size={20} />
              </a>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default function Home() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [emailErrorContact, setEmailErrorContact] = useState("");

  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isSubscribeSuccess, setIsSubscribeSuccess] = useState(false);
  const [emailErrorSubscribe, setEmailErrorSubscribe] = useState("");

  const [isRequestingPlan, setIsRequestingPlan] = useState(false);
  const [planRequested, setPlanRequested] = useState(false);
  const [emailErrorPlan, setEmailErrorPlan] = useState("");

  const [selectedService, setSelectedService] = useState<any | null>(null);
    
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(3);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setVisibleCount(3);
      } else if (window.innerWidth >= 768) {
        setVisibleCount(2);
      } else {
        setVisibleCount(1);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      if (document.hidden) return;
      setTestimonialIndex((prev) => {
        const maxIndex = testimonials.length - visibleCount;
        return prev >= maxIndex ? 0 : prev + 1;
      });
    }, 4500);
    return () => clearInterval(timer);
  }, [visibleCount, isPaused]);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  
  
  
  const handlePlanRequest = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const email = formData.get("email") as string;
    
    if (!email || !emailRegex.test(email)) {
      setEmailErrorPlan("Please enter a valid email address.");
      return;
    }
    setEmailErrorPlan("");
    setIsRequestingPlan(true);

    formData.append("access_key", "c0573f7d-6191-4374-bc31-ee70ee9fa226");
    formData.append("subject", "New Audit/Plan Request");

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData
      });
      const data = await response.json();
      if (data.success) {
        // Sync to Mailchimp
        fetch("/api/connect/subscribe", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ 
            email,
            firstName: formData.get("name")?.toString().split(" ")[0] || ""
          })
        }).catch(err => console.error("Mailchimp Sync Error:", err));

        setPlanRequested(true);
        form.reset();
      } else {
        setEmailErrorPlan("Something went wrong. Please try again.");
      }
    } catch (error) {
       setEmailErrorPlan("Connection error. Please try again.");
    } finally {
      setIsRequestingPlan(false);
    }
  };

  const handleSubscribe = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const email = formData.get("email") as string;

    if (!email || !emailRegex.test(email)) {
      setEmailErrorSubscribe("Please enter a valid email address.");
      return;
    }
    setEmailErrorSubscribe("");
    setIsSubscribing(true);

    formData.append("access_key", "c0573f7d-6191-4374-bc31-ee70ee9fa226");
    formData.append("subject", "New Newsletter Subscriber");

    try {
      // 1. Submit to Web3Forms for email notification
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        // 2. Submit to Mailchimp (Background)
        fetch("/api/connect/subscribe", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email })
        })
        .then(async r => {
          if (!r.ok) {
            const err = await r.json();
            throw new Error(err.details || err.error || "Mailchimp sync failed");
          }
          console.log("Mailchimp sync success");
        })
        .catch(err => {
          console.error("Mailchimp Sync Error:", err);
        });

        setIsSubscribeSuccess(true);
        form.reset();
        setTimeout(() => setIsSubscribeSuccess(false), 5000);
      } else {
        console.error("Error submitting form", data);
        setEmailErrorSubscribe("Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting form", error);
      setEmailErrorSubscribe("Connection error. Please try again.");
    } finally {
      setIsSubscribing(false);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const email = formData.get("email") as string;

    if (!email || !emailRegex.test(email)) {
      setEmailErrorContact("Please enter a valid email address.");
      return;
    }
    setEmailErrorContact("");
    setIsSubmitting(true);

    formData.append("access_key", "c0573f7d-6191-4374-bc31-ee70ee9fa226");

    try {
      // 1. Submit to Web3Forms for email notification
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        // 2. Submit to Mailchimp (Background)
        const name = (formData.get("name") as string) || "";
        fetch("/api/connect/subscribe", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            firstName: name.split(" ")[0],
            lastName: name.split(" ").slice(1).join(" ")
          })
        }).catch(err => console.error("Mailchimp Sync Error:", err));

        setIsSuccess(true);
        form.reset();
      } else {
        console.error("Error submitting form", data);
        setEmailErrorContact("Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting form", error);
      setEmailErrorContact("Connection error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageWrapper 
      title="Best Shopify Developer, Consultant & SEO Expert | Sheun Hub" 
      description="Need a certified Shopify Developer & SEO Consultant? Sheun Hub provides custom store builds, technical SEO audits, WooCommerce migrations, and speed optimizations for global brands worldwide."
      keywords="best shopify developer, affordable shopify developer, shopify expert reviews, freelance shopify developer, WooCommerce to Shopify migration, Shopify SEO Sprint, Technical Shopify SEO Audit, Custom Shopify Themes, E-commerce CRO, speed optimization"
      canonical="/"
      schema={{
        "@context": "https://schema.org",
        "@type": "ProfilePage",
        "name": "Shopify Developer, SEO Specialist & Growth Expert - Sheun Hub",
        "description": "Scale your brand with a certified Shopify Partner specializing in WooCommerce migrations, technical SEO audits, custom theme development, and performance optimization for e-commerce brands worldwide.",
        "url": "https://sheun.online",
        "mainEntity": {
          "@type": "Person",
          "name": "Sheun Hub",
          "jobTitle": "Shopify Developer & SEO Specialist",
          "url": "https://sheun.online",
          "sameAs": [
            "https://www.linkedin.com/in/sheun-hub-26b876321",
            "https://twitter.com/sheunhub"
          ]
        }
      }}
    >
      {/* Hero Section */}
        <section className="relative flex items-center pt-24 md:pt-36 pb-16 md:pb-24 bg-navy-gradient overflow-hidden">
          {/* Rail Text - Recipe 11 */}
        <div className="absolute left-6 top-1/2 -translate-y-1/2 hidden xl:block z-20">
          <div className="flex flex-col items-center gap-8">
            <div className="w-px h-24 bg-[#8B5CF6]/20" />
            <span className="[writing-mode:vertical-rl] rotate-180 text-[10px] font-bold uppercase tracking-[0.12em] md:tracking-[0.4em] text-white/30">
              Shopify Partner · 2026 Edition
            </span>
          </div>
        </div>

        {/* Dynamic Background - Recipe 7 */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-[60%] h-[60%] bg-[#6D28D9]/20 rounded-full blur-[160px] animate-pulse" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-[#D946EF]/15 rounded-full blur-[150px]" />
          <div className="absolute top-1/2 left-1/2 w-[35%] h-[35%] bg-[#FF6B4A]/10 rounded-full blur-[180px]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_#8B5CF6_1px,_transparent_1px)] bg-[size:60px_60px] opacity-[0.03]" />
        </div>

        <div className="container mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center relative z-10">
          <div className="lg:col-span-7 space-y-8 md:space-y-12">
            <div
              className="space-y-6 md:space-y-8"
            >
              <div className="inline-flex items-center gap-3 bg-white/5 backdrop-blur-xl border border-white/10 px-5 py-2 rounded-full">
                <div className="w-2 h-2 bg-[#FF6B4A] rounded-full animate-ping" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B4A] via-[#D946EF] to-[#8B5CF6] text-[10px] font-bold uppercase tracking-[0.12em] md:tracking-[0.2em]">Available for new projects</span>
              </div>

              <h1 className="hero-h1 font-bold text-white tracking-tight text-balance">
                <span className="block mb-4">
                  <span
                    key="headline-1"
                  >
                    Shopify Development
                  </span>
                  <br />
                  <span
                    key="headline-2"
                  >
                    &amp; <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B4A] via-[#D946EF] to-[#8B5CF6]"><WordRotator words={["Growth", "Scaling", "Speed", "SEO"]} /></span>
                  </span>
                </span>
                <span 
                  className="text-white italic font-serif font-light opacity-40 block"
                >
                  by Sheun Hub.
                </span>
              </h1>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-8">
                {[
                  { title: "Custom Development", desc: "Bespoke Liquid coding.", color: "#8B5CF6", border: "hover:border-[#8B5CF6]/40" },
                  { title: "Store Optimization", desc: "Speed and UX refinement.", color: "#D946EF", border: "hover:border-[#D946EF]/40" },
                  { title: "E-commerce Scaling", desc: "Conversion focused design.", color: "#FF6B4A", border: "hover:border-[#FF6B4A]/40" }
                ].map((item, i) => (
                  <div
                    key={i}
                  >
                    <TiltCard depth={20}>
                      <div className={`bg-white/5 border border-white/10 ${item.border} p-6 rounded-2xl h-full backdrop-blur-sm shadow-xl transition-all`}>
                        <div style={{ color: item.color }} className="text-sm font-bold uppercase tracking-widest mb-1">{item.title}</div>
                        <p className="text-white/70 text-xs italic font-serif">{item.desc}</p>
                      </div>
                    </TiltCard>
                  </div>
                ))}
              </div>

                <p className="text-white/80 text-xl md:text-2xl max-w-xl leading-relaxed font-light font-serif italic">
                  Scale your brand with a specialist in Custom Shopify Development and E-commerce Growth. From custom theme builds to technical store optimization, Sheun Hub helps businesses maximize revenue on the Shopify platform.
                </p>
            </div>

            <div 
              className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6"
            >
              <Link 
                to="/apply" 
                className="w-full sm:w-auto bg-gradient-to-r from-[#FF6B4A] via-[#D946EF] to-[#6D28D9] text-white hover:opacity-95 px-8 md:px-12 py-4 rounded-full font-bold text-base md:text-lg hover:scale-105 transition-all duration-500 shadow-xl shadow-[#D946EF]/25 flex items-center justify-center gap-3 text-center min-h-[48px]"
              >
                Get Started <ArrowRight size={20} />
              </Link>
              <Link 
                to="/services" 
                className="text-white font-bold hover:text-[#D946EF] transition-colors flex items-center gap-2 min-h-[44px] px-3 py-2"
              >
                Explore Services <ChevronDown size={16} />
              </Link>
              <Link to="/portfolio" className="w-full sm:w-auto text-white/80 hover:text-white px-6 py-3 rounded-full font-bold text-base md:text-lg transition-all flex items-center justify-center gap-3 group text-center min-h-[48px]">
                View Portfolio <div className="w-8 h-px bg-white/20 group-hover:w-16 group-hover:bg-[#FF6B4A] transition-all duration-500" />
              </Link>
            </div>

            <div 
              className="flex flex-wrap items-center gap-12 pt-8 border-t border-white/5"
            >
              {[
                { label: "Stores Built", value: "20+" },
                { label: "Rating", value: "5.0 Stars" },
                { label: "Upwork", value: "Top Rated" },
                { label: "Trust", value: "100% Secure" },
              ].map((stat, i) => (
                <div key={i} className="space-y-2">
                  <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/70">{stat.label}</p>
                  <p className="text-2xl font-bold text-white tracking-tight">{stat.value}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative h-[520px] lg:h-[700px] flex items-center justify-center">
            <div className="relative w-full max-w-md aspect-[4/5] rounded-3xl bg-white/5 backdrop-blur-3xl border border-white/10 shadow-2xl overflow-hidden group">
              {/* Optimized Local Hero Store Showcase Image */}
              <img 
                src="/hero-mockup.webp"
                srcSet="/hero-mockup-mobile.webp 380w, /hero-mockup.webp 448w"
                sizes="(max-width: 768px) 380px, 448px" 
                alt="Shopify High Conversion Store Interface - Sheun Hub" 
                width="448"
                height="560"
                fetchPriority="high"
                loading="eager" 
                decoding="async"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent pointer-events-none" />

              {/* Floating Content Card (HTML for crawlability & accessibility) */}
              <div className="absolute bottom-6 left-6 right-6 space-y-4 z-20 pointer-events-auto">
                <div className="flex gap-1.5">
                  {[...Array(5)].map((_, i) => <Star key={i} size={14} className="fill-[#FF6B4A] text-[#FF6B4A]" />)}
                </div>
                <div>
                  <h2 className="text-white font-bold text-3xl sm:text-4xl tracking-tighter leading-tight">
                    Top Rated <br />Shopify Expert.
                  </h2>
                  <p className="text-white/70 text-sm sm:text-base font-serif italic mt-1">
                    Shopify Partner &amp; Lead Systems Engineer
                  </p>
                </div>
                
                <div className="flex items-center justify-between pt-3 border-t border-white/10">
                  <div className="flex gap-3">
                    <a href="https://www.linkedin.com/in/sheun-hub-26b876321" target="_blank" rel="noopener noreferrer" className="p-2 bg-white/10 hover:bg-[#6D28D9] hover:text-white rounded-full text-white transition-all flex items-center justify-center overflow-hidden" aria-label="LinkedIn profile">
                      <LinkedInIcon className="w-4 h-4 text-[#0A66C2]" />
                    </a>
                    <a href="https://www.upwork.com/freelancers/~017eb19011cd354946" target="_blank" rel="noopener noreferrer" className="p-2 bg-white/10 hover:bg-[#6D28D9] hover:text-white rounded-full text-white transition-all flex items-center justify-center overflow-hidden" aria-label="Upwork profile">
                      <UpworkIcon className="w-4 h-4 text-[#14A800]" />
                    </a>
                    <a href="mailto:sheunhost@gmail.com" className="p-2 bg-white/10 hover:bg-[#6D28D9] hover:text-white rounded-full text-white transition-all flex items-center justify-center overflow-hidden" aria-label="Email Sheun Hub">
                      <GmailIcon className="w-4 h-4 text-[#EA4335]" />
                    </a>
                  </div>
                  <span className="text-[11px] font-mono text-cyan-300 bg-cyan-950/60 border border-cyan-800/40 px-2.5 py-1 rounded-lg">
                    Interactive 3D
                  </span>
                </div>
              </div>
            </div>

            {/* Floating Metric Card */}
            <div className="absolute -top-8 -right-6 w-56 h-52 bg-white/5 backdrop-blur-2xl border border-white/10 rounded-3xl shadow-2xl p-1 hidden sm:block z-30 pointer-events-none">
              <div className="w-full h-full rounded-3xl bg-navy/80 flex flex-col items-center justify-center gap-4 border border-white/5 p-4 text-center">
                <div className="w-14 h-14 bg-gradient-to-br from-[#FF6B4A]/20 via-[#D946EF]/20 to-[#6D28D9]/20 rounded-2xl flex items-center justify-center text-[#FF6B4A] shadow-inner">
                  <ShoppingCart size={28} />
                </div>
                <div>
                  <p className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B4A] via-[#D946EF] to-[#8B5CF6] font-bold text-2xl tracking-tighter">+142%</p>
                  <p className="text-white/40 text-[9px] uppercase font-bold tracking-[0.25em] mt-0.5">Avg. ROI Increase</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40">
          <span className="text-[10px] font-bold uppercase tracking-widest text-white">Scroll</span>
          <div className="w-px h-12 bg-gradient-to-b from-[#FF6B4A] via-[#D946EF] to-transparent" />
        </div>
      </section>

    <div className="py-8 bg-[#0F172A] border-b border-white/5 overflow-hidden">
      <Marquee speed={40}>
        {['SHOPIFY EXPERT', 'CUSTOM LIQUID', 'HEADLESS COMMERCE', 'CRO SPECIALIST', 'SPEED OPTIMIZATION', 'TECHNICAL SEO', 'REACT & VITE', 'TAILWIND CSS'].map((text, i) => (
          <div key={i} className="flex items-center gap-16">
            <span className="text-white/70 font-bold text-sm tracking-[0.4em] uppercase">{text}</span>
            <span className="text-[#D946EF]/50 text-xl font-bold">•</span>
          </div>
        ))}
      </Marquee>
    </div>

    {/* Credibility Bar (The Badges Pool) */}
    <section className="py-12 bg-[#F8FAFC] dark:bg-navy border-b border-[#E2E8F0] relative overflow-hidden">
      {/* Pool Wall 3-Color Gradient Top Rim */}
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#FF6B4A] via-[#D946EF] to-[#6D28D9]" />

      {/* Background Dots */}
      <div className="absolute inset-0 bg-[radial-gradient(#E2E8F0_1px,transparent_1px)] bg-[size:24px_24px] opacity-40 pointer-events-none"></div>
      
      <div className="container mx-auto px-6 max-w-7xl relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { label: "Shopify Partner", sub: "Verified & Certified", icon: ShieldCheck, color: "#6D28D9" },
            { label: "Upwork Top Rated", sub: "100% Success Rate", icon: Trophy, color: "#D946EF" },
            { label: "5.0 Star Expert", sub: "Loved By Merchants", icon: Star, color: "#FF6B4A" },
            { label: "100% Satisfaction", sub: "Money-Back Guarantee", icon: CheckCircle2, color: "#8B5CF6" }
          ].map((badge, idx) => (
            <div
              key={idx}
              className="flex items-center gap-4 p-5 rounded-2xl bg-white dark:bg-white/5 border border-[#E2E8F0] dark:border-white/10 shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-[0_12px_24px_-10px_rgba(0,0,0,0.05)] transition-all group"
            >
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-110"
                style={{ backgroundColor: `${badge.color}15`, color: badge.color }}
              >
                <badge.icon size={22} className={badge.icon === Star ? "fill-current" : ""} />
              </div>
              <div>
                <div className="font-bold text-[#0F172A] dark:text-white text-sm leading-tight font-sans">{badge.label}</div>
                <p className="text-[11px] text-[#71717a] dark:text-white/60 font-medium mt-0.5">{badge.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Progressive 3D Shopify Performance Engine (Isolated, Non-LCP, WebGL-Optimized) */}
    <IntersectionWrapper fallback={<div className="h-48 bg-navy/5" />}>
      <Shopify3DEngine />
    </IntersectionWrapper>

    {/* Pain Points Section */}
    <ScrollReveal>
      <section className="py-32 bg-[#FFFFFF] dark:bg-navy relative overflow-hidden border-b border-[#E2E8F0]">
        {/* Glow Effects */}
        <div className="absolute top-1/4 left-1/3 w-[500px] h-[500px] bg-[#6D28D9]/5 rounded-full blur-[120px] pointer-events-none -z-10"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#E2E8F020_1px,transparent_1px),linear-gradient(to_bottom,#E2E8F020_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] -z-10"></div>

        <div className="container mx-auto px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto text-center mb-24 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F4F4F5] dark:bg-white/5 border border-[#E2E8F0] dark:border-white/10 text-xs font-semibold uppercase tracking-wider text-[#0F172A] dark:text-white/80 mb-2">
              <AlertTriangle size={14} className="text-[#F59E0B]" />
              The Reality of E-Commerce
            </div>
            <h2 className="section-h2 font-bold text-navy dark:text-white tracking-tight">
              Is your Shopify store <br />
              <span className="text-[#8B5CF6] underline decoration-[#8B5CF6]/20 underline-offset-8 italic font-serif font-light">working for you?</span>
            </h2>
            <p className="text-[#71717a] dark:text-white/70 text-lg sm:text-xl font-serif italic max-w-2xl mx-auto leading-relaxed">
              Most Shopify stores fail not because the product is bad, but because the technical and user-experience foundation is weak.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              { 
                title: "Your store is slow.", 
                desc: "Every second of load time reduces conversions by 7%. If your store takes more than 3 seconds to load, you are literally throwing money away and hurting search rankings.",
                icon: Zap,
                color: "#8B5CF6",
                stat: "7% Loss",
                statLabel: "per second delay"
              },
              { 
                title: "Low conversion rate.", 
                desc: "Traffic is expensive. If you are getting visitors but no checkout conversions, your UI/UX is creating friction that stops prospective buyers from clicking 'Buy Now'.",
                icon: Target,
                color: "#2563EB",
                stat: "1.5% average",
                statLabel: "standard Shopify CRO"
              },
              { 
                title: "Broken layouts.", 
                desc: "A distorted image or a broken checkout button on mobile devices kills buyer trust instantly. Modern consumers do not buy from stores that look unprofessional.",
                icon: AlertCircle,
                color: "#EF4444",
                stat: "85% customers",
                statLabel: "abandon on broken design"
              },
              { 
                title: "No technical partner.", 
                desc: "Spending countless hours trying to fix complex Liquid code, app conflicts, or checkout errors yourself is a waste of your time. You should focus entirely on scaling.",
                icon: Code2,
                color: "#09090b",
                stat: "100% focused",
                statLabel: "on business growth"
              }
            ].map((point, i) => (
              <div 
                key={i} 
                
                className="p-8 sm:p-10 bg-white dark:bg-white/5 rounded-3xl border border-[#E2E8F0] dark:border-white/10 shadow-[0_2px_12px_rgba(0,0,0,0.01)] hover:shadow-[0_20px_40px_-15px_rgba(9,9,11,0.08)] transition-all space-y-6 relative overflow-hidden"
              >
                <div className="flex items-start justify-between gap-4">
                  <div 
                    className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${point.color}10`, color: point.color }}
                  >
                    <point.icon size={26} />
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-[#0F172A] dark:text-white font-mono">{point.stat}</div>
                    <div className="text-[10px] text-[#71717a] dark:text-white/60 font-medium uppercase tracking-wider mt-0.5">{point.statLabel}</div>
                  </div>
                </div>
                <div className="space-y-3">
                  <h3 className="text-2xl font-bold text-[#0F172A] dark:text-white tracking-tight">{point.title}</h3>
                  <p className="text-[#71717a] dark:text-white/70 text-sm leading-relaxed">{point.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </ScrollReveal>

    <ScrollReveal>
        <section className="py-32 bg-white dark:bg-navy relative overflow-hidden border-y border-navy/5">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto space-y-12">
            <div className="text-center space-y-6">
              <p className="text-[#8B5CF6] text-[10px] font-bold uppercase tracking-[0.3em]">Free Strategy Plan</p>
              <h2 className="text-4xl md:text-6xl font-bold text-navy dark:text-white tracking-tight">
                Scale Your <span className="italic font-serif font-light text-navy dark:text-white/40">Sales</span>.
              </h2>
              <p className="text-navy dark:text-white/60 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
                Enter your store details below and I will personally review your site and send you a custom, 3-step action plan to increase your conversion rate.
              </p>
            </div>

            <div className="bg-light dark:bg-white/5 p-8 md:p-12 rounded-3xl shadow-xl border border-navy/5 dark:border-white/10 relative overflow-hidden">
              {/* Top Wall 3-Color Gradient Separator */}
              <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#FF6B4A] via-[#D946EF] to-[#6D28D9]" />
              {!planRequested ? (
                <form onSubmit={handlePlanRequest} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-navy dark:text-white ml-4">Your Name *</label>
                      <input 
                        type="text" name="name" 
                        required
                        placeholder="John Doe" 
                        className="w-full bg-white dark:bg-white/10 border-2 border-navy/5 dark:border-white/10 rounded-full py-4 px-6 focus:border-[#8B5CF6] outline-none transition-all text-navy dark:text-white placeholder:text-navy/40 dark:placeholder:text-white/40"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-navy dark:text-white ml-4">Email Address *</label>
                      <input 
                        type="email" 
                        name="email" 
                        required
                        placeholder="john@example.com" 
                        className={`w-full bg-white dark:bg-white/10 border-2 ${emailErrorPlan ? 'border-red-500' : 'border-navy/5 dark:border-white/10'} rounded-full py-4 px-6 focus:border-[#8B5CF6] outline-none transition-all text-navy dark:text-white placeholder:text-navy/40 dark:placeholder:text-white/40`}
                      />
                      {emailErrorPlan && <p className="text-red-500 text-xs ml-4">{emailErrorPlan}</p>}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-navy dark:text-white ml-4">Store URL *</label>
                    <input 
                      type="text" 
                      name="store_url" 
                      required
                      placeholder="yourstore.com" 
                      className="w-full bg-white dark:bg-white/10 border-2 border-navy/5 dark:border-white/10 rounded-full py-4 px-6 focus:border-[#8B5CF6] outline-none transition-all text-navy dark:text-white placeholder:text-navy/40 dark:placeholder:text-white/40"
                    />
                  </div>
                  <button 
                    type="submit"
                    disabled={isRequestingPlan}
                    className="w-full bg-gradient-to-r from-[#FF6B4A] via-[#D946EF] to-[#6D28D9] text-white py-5 rounded-full font-bold text-lg hover:opacity-95 transition-opacity disabled:opacity-50 flex items-center justify-center gap-3 shadow-lg shadow-[#D946EF]/20"
                  >
                    {isRequestingPlan ? (
                      <>
                        <RefreshCw className="animate-spin" size={20} />
                        Creating your request...
                      </>
                    ) : (
                      <>
                        <Zap size={20} />
                        Get My Free Growth Plan
                      </>
                    )}
                  </button>
                  <p className="text-center text-xs text-navy dark:text-white/40 mt-4 flex justify-center items-center gap-2">
                    <Lock size={12} /> 100% Secure. No spam, ever.
                  </p>
                </form>
              ) : (
                <div className="text-center space-y-6 py-12">
                  <div className="w-20 h-20 bg-[#6D28D9]/20 rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle2 size={40} className="text-[#8B5CF6]" />
                  </div>
                  <h3 className="text-3xl font-bold text-navy dark:text-white tracking-tight">Congratulations! Request Received!</h3>
                  <p className="text-navy dark:text-white/60 text-lg max-w-md mx-auto leading-relaxed">
                    Your form was submitted. You will receive a message with further steps from <span className="font-bold">sheunhost@gmail.com</span> shortly. We will review your store and email your custom Growth Plan within 24-48 hours. Let's scale your brand!
                  </p>
                </div>
              )}
            </div>
            
            <div className="bg-navy p-10 md:p-16 rounded-3xl shadow-2xl flex flex-col md:flex-row items-center justify-between gap-10 translate-y-6 relative overflow-hidden border border-white/10">
               {/* Pool Wall Top Rim */}
               <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#FF6B4A] via-[#D946EF] to-[#6D28D9]" />
               <div className="absolute top-0 right-0 p-8 opacity-5">
                  <TrendingUp size={150} className="text-[#8B5CF6]" />
               </div>
              <div className="space-y-4 text-center md:text-left relative z-10">
                <h3 className="text-3xl font-bold text-white tracking-tight">Want to talk immediately?</h3>
                <p className="text-white/60 text-lg max-w-lg font-serif italic">
                  Skip the line and book a free 1-on-1 strategy call with us to discuss your custom store build.
                </p>
              </div>
              <button onClick={openCalendlyPopup} className="shrink-0 relative z-10 bg-[#FF6B4A] hover:bg-[#ff7e60] text-white px-10 py-5 rounded-full font-bold text-lg hover:scale-105 transition-all duration-300 shadow-xl shadow-[#FF6B4A]/30 flex items-center gap-3 w-full md:w-auto justify-center cursor-pointer">
                Book Strategy Call <ArrowRight size={20} />
              </button>
            </div>

          </div>
        </div>
      </section>
    </ScrollReveal>

    <IntersectionWrapper fallback={<div className="h-64 bg-navy/5" />}>
      <ImpactMetrics />
    </IntersectionWrapper>

    {/* Credibility & Comparison Section */}
    <ScrollReveal>
      <section className="py-32 bg-white dark:bg-navy relative overflow-hidden border-b border-[#E2E8F0]">
        {/* Subtle decorative grid lines */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#E2E8F015_1px,transparent_1px),linear-gradient(to_bottom,#E2E8F015_1px,transparent_1px)] bg-[size:4rem_4rem] pointer-events-none -z-10"></div>
        <div className="absolute top-1/2 left-1/2 w-[600px] h-[600px] bg-[#6D28D9]/5 rounded-full blur-[140px] pointer-events-none -translate-x-1/2 -translate-y-1/2 -z-10"></div>

        <div className="container mx-auto px-6 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-24 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F4F4F5] dark:bg-white/10 border border-[#E2E8F0] dark:border-white/10 text-xs font-semibold uppercase tracking-wider text-[#0F172A] dark:text-white mb-2">
              The Comparison
            </div>
            <h2 className="text-5xl md:text-7xl font-bold text-navy dark:text-white tracking-tighter">
              Why brands choose <br />
              <span className="text-[#8B5CF6] underline decoration-[#8B5CF6]/20 underline-offset-8 italic font-serif font-light">Sheun Hub over Agencies.</span>
            </h2>
            <p className="text-[#71717a] dark:text-white/70 text-lg sm:text-xl font-serif italic leading-relaxed">
              Finding the "best Shopify developer" shouldn't mean paying agency overheads or gambling on cheap, unreliable freelancers.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
            {/* Cheap Freelancer */}
            <div className="p-8 sm:p-10 rounded-3xl border border-[#E2E8F0] dark:border-white/10 bg-[#F8FAFC] dark:bg-navy/40 space-y-8 flex flex-col opacity-60 grayscale hover:grayscale-0 hover:opacity-100 hover:border-red-200 dark:hover:border-red-500/30 transition-all duration-300">
              <div className="space-y-2">
                <span className="text-xs font-bold uppercase tracking-widest text-[#71717a] dark:text-white/40">Option 01</span>
                <h3 className="text-2xl font-bold text-[#0F172A] dark:text-white tracking-tight">Generic Freelancer</h3>
                <p className="text-red-500 text-xs font-bold uppercase tracking-widest">Low Cost, High Risk</p>
              </div>
              <ul className="space-y-4 flex-grow border-t border-[#E2E8F0] dark:border-white/10 pt-6">
                <li className="flex items-start gap-3 text-[#71717a] dark:text-white/60 text-sm leading-relaxed">
                  <X size={18} className="text-red-500 shrink-0 mt-0.5" />
                  Struggles with communication, frequently misinterpreting complex requirements leading to costly re-dos.
                </li>
                <li className="flex items-start gap-3 text-[#71717a] dark:text-white/60 text-sm leading-relaxed">
                  <X size={18} className="text-red-500 shrink-0 mt-0.5" />
                  Relies on generic, bloated templates that fail to capture your unique brand identity or build trust.
                </li>
                <li className="flex items-start gap-3 text-[#71717a] dark:text-white/60 text-sm leading-relaxed">
                  <X size={18} className="text-red-500 shrink-0 mt-0.5" />
                  Plagued by unreliable timelines, sudden disappearances, and a lack of professional accountability.
                </li>
                <li className="flex items-start gap-3 text-[#71717a] dark:text-white/60 text-sm leading-relaxed">
                  <X size={18} className="text-red-500 shrink-0 mt-0.5" />
                  Focuses solely on getting the code working without any understanding of what actually drives sales.
                </li>
              </ul>
              <div className="pt-6 border-t border-[#E2E8F0] dark:border-white/10">
                <p className="text-[#71717a] dark:text-white/40 font-bold text-xs uppercase tracking-wider">Hidden Cost: <span className="text-red-500 font-mono">Broken Stores</span></p>
              </div>
            </div>

            {/* Sheun Hub (The Expert) */}
            <div className="p-8 sm:p-10 rounded-3xl bg-[#09090b] text-white space-y-8 flex flex-col relative shadow-[0_20px_50px_rgba(0,0,0,0.15)] lg:scale-105 z-10 border border-[#8B5CF6]/30">
              {/* Badge */}
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#6D28D9] text-white text-[10px] font-bold uppercase tracking-[0.25em] px-6 py-2 rounded-full shadow-lg">
                Most Reliable Choice
              </div>
              {/* Internal dotted background decoration */}
              <div className="absolute inset-0 bg-[radial-gradient(rgba(139,92,246,0.15)_1px,transparent_1px)] bg-[size:16px_16px] rounded-3xl pointer-events-none"></div>

              <div className="space-y-2 relative z-10">
                <span className="text-xs font-bold uppercase tracking-widest text-[#8B5CF6]">Partner Option</span>
                <h3 className="text-3xl font-bold text-white tracking-tight">Sheun Hub</h3>
                <p className="text-[#8B5CF6] text-xs font-bold uppercase tracking-widest">Professional Shopify Partner</p>
              </div>
              <ul className="space-y-4 flex-grow border-t border-white/10 pt-6 relative z-10">
                <li className="flex items-start gap-3 text-white/90 text-sm leading-relaxed">
                  <CheckCircle2 size={18} className="text-[#8B5CF6] shrink-0 mt-0.5" />
                  Work directly with a dedicated Shopify specialist, ensuring no details are lost in translation.
                </li>
                <li className="flex items-start gap-3 text-white/90 text-sm leading-relaxed">
                  <CheckCircle2 size={18} className="text-[#8B5CF6] shrink-0 mt-0.5" />
                  Bespoke, lightweight Liquid code built from the ground up for maximum speed and technical SEO.
                </li>
                <li className="flex items-start gap-3 text-white/90 text-sm leading-relaxed">
                  <CheckCircle2 size={18} className="text-[#8B5CF6] shrink-0 mt-0.5" />
                  Every technical decision is driven by conversion psychology, ROI, and commercial growth metrics.
                </li>
                <li className="flex items-start gap-3 text-white/90 text-sm leading-relaxed">
                  <CheckCircle2 size={18} className="text-[#8B5CF6] shrink-0 mt-0.5" />
                  Clear, upfront project pricing that eliminates the hidden fees and bloated overhead of traditional agencies.
                </li>
                <li className="flex items-start gap-3 text-white/90 text-sm leading-relaxed">
                  <CheckCircle2 size={18} className="text-[#8B5CF6] shrink-0 mt-0.5" />
                  Experience highly proactive, crystal-clear communication and rapid feedback loops throughout the project.
                </li>
              </ul>
              <div className="pt-6 border-t border-white/10 relative z-10">
                <p className="text-white/80 font-bold text-xs uppercase tracking-wider">Result: <span className="text-[#8B5CF6] font-mono">Profitable Scaled Store</span></p>
              </div>
            </div>

            {/* Big Agency */}
            <div className="p-8 sm:p-10 rounded-3xl border border-[#E2E8F0] dark:border-white/10 bg-[#F8FAFC] dark:bg-navy/40 space-y-8 flex flex-col opacity-60 grayscale hover:grayscale-0 hover:opacity-100 hover:border-red-200 dark:hover:border-red-500/30 transition-all duration-300">
              <div className="space-y-2">
                <span className="text-xs font-bold uppercase tracking-widest text-[#71717a] dark:text-white/40">Option 02</span>
                <h3 className="text-2xl font-bold text-[#0F172A] dark:text-white tracking-tight">Big Digital Agency</h3>
                <p className="text-red-500 text-xs font-bold uppercase tracking-widest">High Cost, Low Attention</p>
              </div>
              <ul className="space-y-4 flex-grow border-t border-[#E2E8F0] dark:border-white/10 pt-6">
                <li className="flex items-start gap-3 text-[#71717a] dark:text-white/60 text-sm leading-relaxed">
                  <X size={18} className="text-red-500 shrink-0 mt-0.5" />
                  Forces you into massive minimum engagement fees and insists on locking you into long-term retainers.
                </li>
                <li className="flex items-start gap-3 text-[#71717a] dark:text-white/60 text-sm leading-relaxed">
                  <X size={18} className="text-red-500 shrink-0 mt-0.5" />
                  Quietly outsources the actual execution of your project to inexperienced junior developers or interns.
                </li>
                <li className="flex items-start gap-3 text-[#71717a] dark:text-white/60 text-sm leading-relaxed">
                  <X size={18} className="text-red-500 shrink-0 mt-0.5" />
                  Stifles progress with overwhelming bureaucracy, account managers, and slow support-ticket systems.
                </li>
                <li className="flex items-start gap-3 text-[#71717a] dark:text-white/60 text-sm leading-relaxed">
                  <X size={18} className="text-red-500 shrink-0 mt-0.5" />
                  Treats your ambitious brand as just another number in their massive queue rather than a strategic partner.
                </li>
              </ul>
              <div className="pt-6 border-t border-[#E2E8F0] dark:border-white/10">
                <p className="text-[#71717a] dark:text-white/40 font-bold text-xs uppercase tracking-wider">Hidden Cost: <span className="text-red-500 font-mono">Inefficiency</span></p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </ScrollReveal>

    {/* Services Preview - Visible Grid Recipe */}
    <ScrollReveal>
      <section className="py-32 bg-navy-gradient relative" id="services">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-24">
            <div className="space-y-6">
              <p className="text-[#8B5CF6] text-[10px] font-bold uppercase tracking-[0.4em]">Expertise</p>
              <h2 className="text-5xl md:text-8xl font-bold text-white tracking-tighter leading-[0.85]">
                Crafting <span className="italic font-serif font-light text-white/40">Digital</span><br />
                Commerce.
              </h2>
            </div>
            <Link to="/services" className="group flex items-center gap-4 text-white font-bold text-lg">
              View All Services <div className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center group-hover:bg-[#6D28D9] group-hover:text-white transition-all">
                <ArrowRight size={20} />
              </div>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/10 rounded-2xl overflow-hidden relative">
            {/* Top Wall 3-Color Gradient Separator */}
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#FF6B4A] via-[#D946EF] to-[#6D28D9] z-20" />
            {[
              {
                title: "Store Setup",
                desc: "Launch your brand instantly with a high-converting, deeply optimized Shopify store designed to maximize sales from day one.",
                fullDesc: "Launch your brand with a professional, high-converting Shopify store. We handle everything from theme selection and customization to essential app integrations and payment gateway setup.",
                icon: ShoppingBag,
                tags: ["Dropshipping", "Branding", "Launch"],
                timeline: "1-2 Weeks",
                price: "$999",
                color: "#8B5CF6",
                tagColor: "text-[#8B5CF6] bg-[#6D28D9]/15",
                roadmap: [
                  { title: "Discovery & Strategy", desc: "Understanding your products, niche, and target audience." },
                  { title: "Theme Selection & Branding", desc: "Choosing the right foundation and applying your visual identity." },
                  { title: "Store Configuration", desc: "Setting up shipping, taxes, payments, and essential apps." },
                  { title: "Product Import & SEO", desc: "Optimizing product data for search engines and conversions." },
                  { title: "Testing & Launch", desc: "Rigorous quality checks before going live to the world." }
                ]
              },
              {
                title: "Custom Dev",
                desc: "Increase conversion rates with bespoke features, custom checkout logic, and lightning-fast custom Liquid coding.",
                fullDesc: "Go beyond standard theme limitationsWe build custom Liquid sections, unique product page features, and complex logic that sets your store apart from the competition.",
                icon: Code2,
                tags: ["Liquid", "API", "Performance"],
                timeline: "2-4 Weeks",
                price: "$1,499",
                color: "#D946EF",
                tagColor: "text-[#D946EF] bg-[#D946EF]/15",
                roadmap: [
                  { title: "Technical Audit", desc: "Reviewing your current code and identifying bottlenecks." },
                  { title: "Feature Scoping", desc: "Defining the exact functionality and user experience." },
                  { title: "Development Phase", desc: "Writing clean, efficient Liquid and JavaScript code." },
                  { title: "Integration & Testing", desc: "Ensuring custom features work seamlessly with your apps." },
                  { title: "Deployment", desc: "Pushing changes to your live store with zero downtime." }
                ]
              },
              {
                title: "Migrations",
                desc: "Safely upgrade to Shopify with absolutely zero downtime, preserving your hard-earned SEO rankings and customer data.",
                fullDesc: "Switching platforms shouldn't be scaryWe ensure a 100% safe migration of your products, customers, orders, and most importantly, your SEO rankings.",
                icon: Zap,
                tags: ["Data", "SEO", "Zero Downtime"],
                timeline: "2-3 Weeks",
                price: "$1,999",
                color: "#FF6B4A",
                tagColor: "text-[#FF6B4A] bg-[#FF6B4A]/15",
                roadmap: [
                  { title: "Data Mapping", desc: "Planning how data from your old platform fits into Shopify." },
                  { title: "Migration Setup", desc: "Configuring secure data transfer protocols." },
                  { title: "Transfer & Validation", desc: "Moving data and verifying every single record." },
                  { title: "SEO Preservation", desc: "Setting up 301 redirects to keep your Google rankings." },
                  { title: "Final Cutover", desc: "Switching your domain and launching on Shopify." }
                ]
              }
            ].map((service, i) => (
              <motion.button
                key={i}
                onClick={() => {
                  setSelectedService(service);
                }}
                className="p-6 md:p-8 lg:p-12 bg-navy space-y-6 md:space-y-10 group hover:bg-white/[0.02] transition-all cursor-pointer text-center w-full block border-r border-b border-white/5 flex flex-col items-center overflow-hidden"
              >
                <div 
                  className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-500 mx-auto transform-gpu"
                  style={{ color: service.color }}
                >
                  <service.icon size={32} />
                </div>
                <div className="space-y-4 md:space-y-6">
                  <h3 className="text-[clamp(1.5rem,2.5vw,2.25rem)] font-bold text-white tracking-tight line-clamp-1">{service.title}</h3>
                  <p className="text-[clamp(1rem,1.5vw,1.125rem)] text-white/40 leading-relaxed font-light font-serif italic line-clamp-3">{service.desc}</p>
                </div>
                <div className="flex flex-wrap justify-center gap-3 pt-6 md:pt-8 border-t border-white/5 w-full">
                  {service.tags.map((tag, j) => (
                    <span key={j} className={`text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-2 rounded-full ${service.tagColor}`}>
                      {tag}
                    </span>
                  ))}
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </section>
    </ScrollReveal>

    {/* Service Detail Modal */}
      <AnimatePresence>
        {selectedService && (
          <ServiceModal
            service={selectedService}
            onClose={() => setSelectedService(null)}
          />
        )}
      </AnimatePresence>

      {/* Testimonials Slider */}
      <ScrollReveal>
        <section className="py-32 bg-light dark:bg-navy relative overflow-hidden border-t border-navy/5 dark:border-white/5">
        <div className="absolute top-0 right-0 p-8 opacity-5 pointer-events-none">
          <Quote size={100} />
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center space-y-6 mb-16">
            <p className="text-navy dark:text-white/40 text-[10px] font-bold uppercase tracking-[0.3em]">Client Success</p>
            <h2 className="text-4xl md:text-5xl font-bold text-navy dark:text-white tracking-tight">
              Trusted by <span className="italic font-serif font-light text-navy dark:text-white/40">Founders</span>.
            </h2>
          </div>

          
          {/* Slider Viewport Container */}
          <div 
            className="relative max-w-7xl mx-auto overflow-hidden px-1 py-4"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <motion.div
              className="flex flex-row flex-nowrap"
              animate={{ x: `-${testimonialIndex * (100 / visibleCount)}%` }}
              transition={{ type: "spring", stiffness: 80, damping: 20 }}
            >
              {testimonials.map((testimonial, idx) => (
                <div
                  key={idx}
                  className="shrink-0 transition-opacity duration-300 px-3"
                  style={{ width: `${100 / visibleCount}%` }}
                >
                  <div className="bg-white dark:bg-navy/80 p-8 rounded-3xl shadow-xl border border-navy/5 dark:border-white/5 flex flex-col h-full justify-between min-h-[340px] relative overflow-hidden">
                    {/* Pool Wall 3-Color Gradient Top Rim */}
                    <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#FF6B4A] via-[#D946EF] to-[#6D28D9]" />
                    <div>
                      <div className="flex items-center justify-between mb-6">
                        <div className="w-10 h-10 bg-gradient-to-br from-[#FF6B4A]/10 via-[#D946EF]/10 to-[#6D28D9]/10 text-[#D946EF] rounded-full flex items-center justify-center shadow-sm">
                          <Quote size={16} className="rotate-180" />
                        </div>
                        <div className="flex items-center gap-1">
                          {[...Array(testimonial.rating || 5)].map((_, j) => (
                            <Star key={j} size={14} fill="currentColor" className="text-[#FF6B4A]" />
                          ))}
                        </div>
                      </div>

                      <p className="text-navy dark:text-white/80 font-serif italic text-[15px] leading-relaxed mb-6">
                        "{testimonial.content}"
                      </p>
                    </div>

                    <div className="border-t border-navy/5 dark:border-white/5 pt-4">
                      <h3 className="text-navy dark:text-white font-bold text-base leading-tight">{testimonial.name}</h3>
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Navigation Controls */}
          <div className="flex items-center justify-center gap-6 mt-12 relative z-20">
            <button
              onClick={() => {
                setTestimonialIndex((prev) => {
                  const maxIndex = testimonials.length - visibleCount;
                  return prev <= 0 ? maxIndex : prev - 1;
                });
              }}
              className="w-12 h-12 rounded-full border border-navy/10 dark:border-white/10 flex items-center justify-center hover:bg-navy hover:text-white dark:hover:bg-white dark:hover:text-navy text-navy dark:text-white transition-all cursor-pointer bg-white dark:bg-navy shadow-md"
              aria-label="Previous testimonial"
            >
              <ChevronLeft size={20} />
            </button>
            
            {/* Pagination Dots */}
            <div className="flex items-center gap-2">
              {[...Array(testimonials.length - visibleCount + 1)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => setTestimonialIndex(i)}
                  className={cn(
                    "h-2.5 rounded-full transition-all duration-300 cursor-pointer",
                    testimonialIndex === i 
                      ? "w-8 bg-gradient-to-r from-[#FF6B4A] via-[#D946EF] to-[#6D28D9]" 
                      : "w-2.5 bg-navy/10 dark:bg-white/10 hover:bg-navy/30 dark:hover:bg-white/30"
                  )}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>

            <button
              onClick={() => {
                setTestimonialIndex((prev) => {
                  const maxIndex = testimonials.length - visibleCount;
                  return prev >= maxIndex ? 0 : prev + 1;
                });
              }}
              className="w-12 h-12 rounded-full border border-navy/10 dark:border-white/10 flex items-center justify-center hover:bg-navy hover:text-white dark:hover:bg-white dark:hover:text-navy text-navy dark:text-white transition-all cursor-pointer bg-white dark:bg-navy shadow-md"
              aria-label="Next testimonial"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
</section>
    </ScrollReveal>

    

    {/* Call to Action Section */}
    <ScrollReveal>
      <section className="py-32 bg-navy relative overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center space-y-12">
            <h2 className="section-h2 font-bold text-white tracking-tight">
              Ready to scale <br />
              <span className="text-[#8B5CF6] italic font-serif font-light">Your Shopify store?</span>
            </h2>
            <p className="text-white/60 text-xl font-serif italic max-w-2xl mx-auto leading-relaxed">
              Join the high-performing brands that have transformed their e-commerce presence with bespoke development and strategic growth.
            </p>
            <div className="pt-8">
              <Link to="/apply" className="inline-flex items-center gap-3 bg-[#6D28D9] text-white hover:bg-[#8B5CF6] font-bold px-8 md:px-12 py-4 rounded-full hover:scale-105 transition-all duration-300 text-lg shadow-2xl shadow-[#6D28D9]/30 min-h-[48px]">
                Apply for Growth Plan <ArrowRight size={24} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </ScrollReveal>

    {/* Why Partner With Us */}
    <ScrollReveal>
      <section className="py-24 md:py-32 bg-navy text-white relative overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="space-y-8 md:space-y-10">
              <h2 className="section-h2 font-bold tracking-tight">
                The Sheun Hub <br />
                <span className="text-[#8B5CF6] italic font-serif font-light">Advantage</span>.
              </h2>
              <p className="text-white/60 text-xl max-w-lg leading-relaxed font-serif italic">
                We don't just build stores; we build profitable eCommerce systems. Combining deep technical expertise with conversion rate psychology.
              </p>
              
              <div className="space-y-6 pt-4">
                {[
                  { title: "Custom Liquid Mastery", desc: "We abandon bloated, cookie-cutter themes in favor of bespoke Liquid architecture. Every line of code is purpose-built for your brand, guaranteeing a unique storefront that doesn't sacrifice performance." },
                  { title: "Speed Obsessive", desc: "We engineer your store with a relentless focus on performance. By optimizing assets and streamlining scripts, we achieve ultra-fast sub-second load times mathematically proven to significantly increase conversion rates and reduce bounce." },
                  { title: "Direct Communication", desc: "Bypass the typical agency bureaucracy and work directly one-on-one with the technical architect. Enjoy clear, proactive updates, fast feedback loops, and zero miscommunications from start to finish." }
                ].map((item, i) => (
                  <div key={i} className="flex gap-6 items-start">
                    <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0 text-[#8B5CF6] mt-1">
                      <CheckCircle2 size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                      <p className="text-white/40 leading-relaxed max-w-md">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden pointer-events-none">
                <img 
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=1200" 
                  alt="Shopify Growth Developer" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-navy via-navy/50 to-transparent" />
              </div>
              <div className="absolute -bottom-10 -left-10 bg-[#6D28D9] text-white p-10 rounded-2xl shadow-2xl border-4 border-navy border-t-0 animate-bounce-slow">
                <p className="text-6xl font-bold tracking-tighter mb-2">100%</p>
                <p className="text-xs uppercase font-bold tracking-widest opacity-80">Job Success Rate</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </ScrollReveal>

    {/* Contact Section - Visible Grid Style */}
    <ScrollReveal>
      <section className="py-32 bg-white dark:bg-navy relative overflow-hidden" id="contact">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-20 items-start">
            <div className="lg:col-span-5 space-y-12">
              <div className="space-y-6">
                <p className="text-navy dark:text-white/40 text-[10px] font-bold uppercase tracking-[0.12em] md:tracking-[0.3em]">Contact</p>
                <h2 className="section-h2 font-bold text-navy dark:text-white tracking-tight leading-none">
                  Let's Start <br />
                  <span className="italic font-serif font-light text-navy dark:text-white/40">Your Project</span>.
                </h2>
                <p className="text-navy dark:text-white/60 text-xl leading-relaxed max-w-md">
                  Ready to transform your eCommerce presence? Fill out the form and we'll get back to you within 24 hours.
                </p>
              </div>

              <div className="space-y-8">
                {[
                  { icon: "gmail", label: "Email", value: "sheunhost@gmail.com" },
                ].map((item, i) => (
                  <div key={i} className="flex flex-col sm:flex-row items-center sm:items-start gap-6 group text-center sm:text-left">
                    <div className="w-14 h-14 bg-light dark:bg-white/10 rounded-2xl flex items-center justify-center text-navy dark:text-white group-hover:bg-[#6D28D9] group-hover:text-white transition-all shrink-0 overflow-hidden">
                      <GmailIcon className="w-6 h-6 text-[#EA4335]" />
                    </div>
                    <div className="min-w-0 flex-grow">
                      <p className="text-[10px] font-bold text-navy dark:text-white/40 uppercase tracking-widest">{item.label}</p>
                      <p className="text-lg font-bold text-navy dark:text-white break-all break-words">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-10 bg-navy text-white rounded-2xl space-y-6 relative overflow-hidden shadow-2xl border border-white/5">
                <div className="absolute top-0 right-0 p-8 opacity-10">
                  <Zap size={100} className="text-[#8B5CF6]" />
                </div>
                <div className="relative z-10 space-y-4">
                  <h3 className="text-xl font-bold">Free Growth Plan</h3>
                  <p className="text-white/40 text-sm leading-relaxed">
                    Not sure what your store needs? We'll provide a comprehensive review of your speed, design, and SEO completely free.
                  </p>
                  <Link to="/contact#contact-form" className="inline-flex items-center gap-2 text-[#8B5CF6] font-bold text-sm border-b border-[#8B5CF6]/20 pb-1 hover:border-[#8B5CF6] transition-all">
                    Claim Your Growth Plan <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="bg-light dark:bg-white/5 p-10 md:p-16 rounded-3xl border border-navy/5 dark:border-white/10 shadow-sm">
                <AnimatePresence mode="wait">
                  {!isSuccess ? (
                    <motion.form 
                      key="home-contact-form"
                      
                      
                      exit={{ opacity: 0, scale: 0.95 }}
                      onSubmit={handleSubmit} 
                      className="space-y-8"
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                        <div className="space-y-3">
                          <label htmlFor="contact-name" className="text-[10px] font-bold text-navy dark:text-white/40 uppercase tracking-widest ml-4">Full Name</label>
                          <input
                            id="contact-name"
                            required
                            type="text"
                            name="name"
                            aria-label="Full Name"
                            placeholder="John Doe"
                            className="w-full bg-white dark:bg-white/10 border-b-2 border-navy/5 dark:border-white/10 rounded-3xl py-5 px-8 focus:border-[#8B5CF6] outline-none transition-all font-medium text-navy dark:text-white placeholder:text-navy/40 dark:placeholder:text-white/40"
                          />
                        </div>
                        <div className="space-y-3">
                          <label htmlFor="contact-email" className="text-[10px] font-bold text-navy dark:text-white/40 uppercase tracking-widest ml-4">Email Address</label>
                          <input
                            id="contact-email"
                            required
                            type="email"
                            name="email"
                            aria-label="Email Address"
                            placeholder="john@example.com"
                            className={`w-full bg-white dark:bg-white/10 border-b-2 ${emailErrorContact ? 'border-red-500' : 'border-navy/5 dark:border-white/10'} rounded-3xl py-5 px-8 focus:border-[#8B5CF6] outline-none transition-all font-medium text-navy dark:text-white placeholder:text-navy/40 dark:placeholder:text-white/40`}
                          />
                          {emailErrorContact && <p className="text-red-500 text-xs ml-4">{emailErrorContact}</p>}
                        </div>
                      </div>

                      <div className="space-y-3">
                        <label htmlFor="contact-project-type" className="text-[10px] font-bold text-navy dark:text-white/40 uppercase tracking-widest ml-4">Project Type</label>
                        <div className="relative">
                          <select id="contact-project-type" name="project_type" aria-label="Project Type" className="w-full bg-white dark:bg-navy border-b-2 border-navy/5 dark:border-white/10 rounded-3xl py-5 px-8 focus:border-[#8B5CF6] outline-none transition-all appearance-none font-medium text-navy dark:text-white">
                            <option className="bg-white dark:bg-navy text-navy dark:text-white">New Store Build</option>
                            <option className="bg-white dark:bg-navy text-navy dark:text-white">Dropshipping Store</option>
                            <option className="bg-white dark:bg-navy text-navy dark:text-white">Shopify Migration Service</option>
                            <option className="bg-white dark:bg-navy text-navy dark:text-white">Theme Redesign</option>
                            <option className="bg-white dark:bg-navy text-navy dark:text-white">Bug Fix</option>
                            <option className="bg-white dark:bg-navy text-navy dark:text-white">Free Growth Plan</option>
                          </select>
                          <ChevronDown className="absolute right-8 top-1/2 -translate-y-1/2 text-navy dark:text-white/40 pointer-events-none" size={20} />
                        </div>
                      </div>

                      <div className="space-y-3">
                        <label htmlFor="contact-message" className="text-[10px] font-bold text-navy dark:text-white/40 uppercase tracking-widest ml-4">Message</label>
                        <textarea
                          id="contact-message"
                          required
                          name="message"
                          rows={5}
                          aria-label="Message"
                          placeholder="Tell us about your project goals..."
                          className="w-full bg-white dark:bg-white/10 border-b-2 border-navy/5 dark:border-white/10 rounded-2xl py-6 px-8 focus:border-[#8B5CF6] outline-none transition-all resize-none font-medium text-navy dark:text-white placeholder:text-navy/40 dark:placeholder:text-white/40"
                        />
                      </div>

                      <button
                        disabled={isSubmitting}
                        className="w-full bg-navy text-white py-6 rounded-full font-bold text-lg hover:bg-[#6D28D9] transition-all flex items-center justify-center gap-4 group shadow-xl"
                      >
                        {isSubmitting ? (
                          <div className="w-6 h-6 border-4 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                          <>
                            Send Message <Send size={20} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                          </>
                        )}
                      </button>
                    </motion.form>
                  ) : (
                    <div
                      key="home-contact-success"
                      
                      
                      className="text-center py-12 space-y-8"
                    >
                      <div className="w-24 h-24 bg-[#6D28D9] text-white rounded-2xl flex items-center justify-center mx-auto shadow-xl rotate-3">
                        <CheckCircle2 size={48} />
                      </div>
                      <div className="space-y-4">
                        <h3 className="text-3xl lg:text-4xl font-bold text-navy dark:text-white uppercase tracking-tight">Congratulations! Message Received.</h3>
                        <p className="text-navy dark:text-white/60 text-lg max-w-sm mx-auto leading-relaxed">
                          Your message has been submitted. A response will be sent to you through <span className="font-bold">sheunhost@gmail.com</span> shortly. We'll review your inquiry and get back to you personally within 24 hours.
                        </p>
                      </div>
                      <button
                        onClick={() => setIsSuccess(false)}
                        className="text-navy dark:text-white font-bold border-b-2 border-[#8B5CF6] pb-1 hover:text-[#8B5CF6] transition-colors"
                      >
                        Send another message
                      </button>
                    </div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </section>
    </ScrollReveal>

    {/* Newsletter Section */}
    <ScrollReveal>
      <section className="py-24 bg-light dark:bg-navy relative overflow-hidden border-y border-navy/5 dark:border-white/10">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto bg-white dark:bg-white/5 rounded-2xl p-10 md:p-16 shadow-xl border border-navy/5 dark:border-white/10 flex flex-col md:flex-row items-center gap-12">
            <div className="flex-1 space-y-6 text-center md:text-left">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#6D28D9]/10 text-[#8B5CF6] rounded-2xl mb-2 overflow-hidden">
                <GmailIcon className="w-8 h-8 text-[#EA4335]" />
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-navy dark:text-white tracking-tight">
                Join the <span className="italic font-serif font-light text-navy dark:text-white/40">Newsletter</span>
              </h2>
              <p className="text-navy/70 dark:text-white/70 text-lg">
                Get weekly tips on Shopify growth, conversion rate optimization, and eCommerce strategies.
              </p>
            </div>
            <div className="flex-1 w-full">
              <AnimatePresence mode="wait">
                {!isSubscribeSuccess ? (
                  <motion.form 
                    key="newsletter-form"
                    
                    
                    exit={{ opacity: 0, scale: 0.95 }}
                    onSubmit={handleSubscribe} 
                    className="relative flex flex-col sm:flex-row items-center gap-4 sm:gap-0"
                  >
                    <label htmlFor="newsletter-email" className="sr-only">Email address for weekly tips</label>
                    <input 
                      id="newsletter-email"
                      type="email" 
                      name="email" 
                      aria-label="Email address for weekly tips"
                      required
                      placeholder="Enter your email address" 
                      className={`w-full bg-light dark:bg-white/10 border-2 ${emailErrorSubscribe ? 'border-red-500' : 'border-navy/5 dark:border-white/10'} rounded-full py-6 pl-8 pr-8 sm:pr-40 focus:border-[#8B5CF6] outline-none transition-all font-medium text-navy dark:text-white placeholder:text-navy/40 dark:placeholder:text-white/40`}
                    />
                    {emailErrorSubscribe && <p className="absolute -bottom-6 left-8 text-red-500 text-xs">{emailErrorSubscribe}</p>}
                    <button 
                      type="submit"
                      disabled={isSubscribing}
                      className="w-full sm:w-auto sm:absolute sm:right-3 bg-navy dark:bg-[#6D28D9] text-white px-8 py-4 sm:py-4 rounded-full font-bold hover:bg-[#6D28D9] dark:hover:bg-[#8B5CF6] hover:text-white transition-colors disabled:opacity-50 min-h-[60px]"
                    >
                      {isSubscribing ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto" />
                      ) : (
                        "Subscribe"
                      )}
                    </button>
                  </motion.form>
                ) : (
                  <div 
                    key="newsletter-success"
                    
                    
                    className="bg-[#6D28D9]/10 border border-[#8B5CF6]/20 rounded-2xl p-8 text-center space-y-4"
                  >
                    <div className="w-16 h-16 bg-[#6D28D9] text-white rounded-full flex items-center justify-center mx-auto shadow-lg">
                      <CheckCircle2 size={32} />
                    </div>
                    <div className="space-y-1">
                      <p className="text-navy dark:text-white font-bold text-xl uppercase tracking-tighter">Congratulations! You're in!</p>
                      <p className="text-navy dark:text-white/60 font-serif italic">Your subscription is successful. A confirmation message from <span className="font-bold">sheunhost@gmail.com</span> will hit your inbox shortly.</p>
                    </div>
                  </div>
                )}
              </AnimatePresence>
              <p className="text-[10px] font-bold text-navy dark:text-white/40 uppercase tracking-widest mt-4 text-center md:text-left">
                100% Privacy. We respect your data.
              </p>
            </div>
          </div>
        </div>
      </section>
    </ScrollReveal>

    {/* CTA Banner - Recipe 2 */}
    <ScrollReveal>
      <section className="py-24 md:py-36 bg-navy-gradient relative overflow-hidden">
        {/* Top Wall 3-Color Gradient Separator */}
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#FF6B4A] via-[#D946EF] to-[#6D28D9]" />
        
        <div className="absolute inset-0 bg-[#6D28D9]/5 opacity-50" />
        <div className="container mx-auto px-6 relative z-10 text-center">
          <div
            className="max-w-5xl mx-auto space-y-12"
          >
            <h2 className="hero-h1 font-bold text-white tracking-tight uppercase">
              Ready to <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B4A] via-[#D946EF] to-[#8B5CF6] italic font-serif font-light lowercase">Grow?</span>
            </h2>
            <p className="text-white/60 text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed font-serif italic">
              Let's build a store that doesn't just look good, but converts visitors into loyal customers.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <Link to="/contact#contact-form" className="w-full sm:w-auto bg-gradient-to-r from-[#FF6B4A] via-[#D946EF] to-[#6D28D9] text-white hover:opacity-95 px-8 md:px-12 py-4 rounded-full font-bold text-lg hover:scale-105 transition-all duration-500 shadow-2xl shadow-[#D946EF]/40 flex items-center justify-center text-center min-h-[48px]">
                Get Your Free Growth Plan
              </Link>
              <Link to="/portfolio" className="w-full sm:w-auto text-white font-bold text-lg flex items-center justify-center gap-4 group text-center min-h-[48px]">
                View Portfolio <div className="w-12 h-px bg-white/20 group-hover:w-20 group-hover:bg-[#FF6B4A] transition-all duration-500" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </ScrollReveal>
  </PageWrapper>
  );
}
